import React from 'react';
import { Grid, pxToRem, useStyles } from '@archipro-design/aria';
import { useVariantOptionState } from '@modules/product/context/VariantOptionContext';
import { useVariantDecision } from '@modules/product/hook/use-variant-decision';
import ColorSelectorPopup from '@modules/product/component/color-selector-popup/ColorSelectorPopup';
import * as S from './ColorPicker.style';
import * as C from './OptionPicker.style';
import { SortDown } from '@archipro-design/icons';
import ProductAttributeEnum from '@modules/product/config/product-attribute';
import type AttributeOption from '@modules/product/type/attribute-option';
import type { BasePickerProps } from '@modules/product/component/variant-selector/OptionPicker';
import OptionLabelWithTooltip from './OptionLabelWithTooltip';
import { useTracker } from '@archipro-website/tracker';

type ColorPickerProps = BasePickerProps & {
    data: {
        options: AttributeOption[];
    };
};

const emptyOption: AttributeOption = {
    ID: 0,
    Code: 'transparent',
    Label: 'Not Applicable',
    Attribute: {
        ID: ProductAttributeEnum.Colour,
        Name: 'Colour',
    },
};

const ColorPicker = ({
    data: { variants, options },
    isGeneric,
}: ColorPickerProps) => {
    const { decision } = useVariantOptionState();
    const { pickOption } = useVariantDecision(variants);
    const selectedOptionID = decision.options[ProductAttributeEnum.Colour] ?? 0;
    const tracker = useTracker();

    const option =
        options.find((opt) => opt.ID === selectedOptionID) ?? emptyOption;

    const items = [...options];

    if (!isGeneric) {
        items.unshift(emptyOption);
    }

    const [popupOpen, setPopupOpen] = React.useState(false);
    const { css } = useStyles<{ popupOpen: boolean }>({ popupOpen });

    // Preload all colour variant thumbnail images in the background
    React.useEffect(() => {
        options.forEach((opt) => {
            if (opt.Image?.Desktop) {
                const img = new window.Image();
                img.src = opt.Image.Desktop;
            }
        });
    }, [options]);

    const togglePopup = (event: React.MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();
        setPopupOpen(!popupOpen);
    };

    return (
        <Grid columns={`${pxToRem(394)} 1fr`} className={css(C.OptionPicker)}>
            <label className={css(C.OptionLabel)}>Colour</label>
            {items.length > 1 ? (
                <div
                    className={css(C.SingleValue, S.ColorOptionSelect)}
                    onClick={togglePopup}
                    aria-haspopup={true}
                >
                    <div className={css(S.OptionIndicator)}>
                        <SortDown />
                    </div>
                    <OptionLabelWithTooltip label={option.Label} />
                    {popupOpen && (
                        <ColorSelectorPopup
                            data={{ options: items, selected: option.ID }}
                            onOptionSelect={(ID) => {
                                tracker.log('DropDownSelect', {
                                    url: new URL(window.location.href),
                                    targetTracker: 'archiproTracker',
                                    data: {
                                        ExtraData: ID,
                                    },
                                });

                                pickOption({
                                    ID,
                                    attributeID: ProductAttributeEnum.Colour,
                                });
                            }}
                            onClose={() => {
                                setPopupOpen(false);
                            }}
                        />
                    )}
                </div>
            ) : (
                <div className={css(C.SingleValue)}>
                    <OptionLabelWithTooltip label={option.Label} />
                </div>
            )}
        </Grid>
    );
};

export default ColorPicker;
